import { ExternalLink, Paperclip } from 'lucide-react';
import type { KeptRef } from '@/components/keep/KeepActions';
import { MailboxKeepActions } from '@/components/provenance/MailboxKeepActions';
import type { MailboxEnvelopeRowData } from '@/utils/mailbox-envelope-row';

export interface MailboxEnvelopeRowProps {
  row: MailboxEnvelopeRowData;
  accountId: number;
  providerLabel: string;
  kept: KeptRef | null;
}

function openWebLink(webLink: string): void {
  window.open(webLink, '_blank', 'noopener,noreferrer');
}

export function MailboxEnvelopeRow({ row, accountId, providerLabel, kept }: MailboxEnvelopeRowProps) {
  const sender = row.fromName ?? row.fromEmail ?? 'Unknown sender';
  const date = row.receivedAt ? new Date(row.receivedAt).toLocaleDateString() : null;
  const webLink = row.webLink;

  return (
    <li className="py-2">
      <div className="flex items-center gap-2">
        <span className="min-w-0 truncate font-medium" title={row.fromEmail ?? undefined}>
          {sender}
        </span>
        {date ? <span className="shrink-0 text-xs text-muted-foreground">{date}</span> : null}
        {row.hasAttachments || row.attachmentCount > 0 ? (
          <span className="inline-flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground">
            <Paperclip className="h-3 w-3" />
            {row.attachmentCount > 0 ? row.attachmentCount : null}
          </span>
        ) : null}
        {row.folders.length > 0 ? (
          <span className="min-w-0 truncate text-xs text-muted-foreground">{row.folders.join(', ')}</span>
        ) : null}
        {webLink ? (
          <button
            type="button"
            onClick={() => openWebLink(webLink)}
            className="ml-auto rounded p-1 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
            aria-label={`Open in ${providerLabel}`}
            title={`Open in ${providerLabel}`}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
      <p className="min-w-0 truncate">{row.subject ?? '(no subject)'}</p>
      {row.snippet ? <p className="min-w-0 truncate text-xs text-muted-foreground">{row.snippet}</p> : null}
      <div className="mt-1">
        <MailboxKeepActions
          scope="message"
          accountId={accountId}
          providerMessageIds={[row.providerMessageId]}
          kept={kept}
        />
      </div>
    </li>
  );
}
